// Org-wide fleet view — every ship across the org that a member has pledged via My Fleet,
// GROUPED by ship. One card per unique hull, with the list of owners who pledged it.
// Leadership-gated (server enforces Roles=Admin,Leader on /api/org/fleet).

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Search, Users, Package, Flag, Clock, FileText, ChevronRight } from 'lucide-react';
import api from '../api/axios';
import styles from '../styles/UserFleet.module.css';
import tficLogo from '../assets/Icons/NEWTFICLOGO_no_bg.png';

// Mirror of Layout.tsx's resolveAvatarUrl — user.AvatarUrl from the DB is typically a
// relative path like "/avatars/abc.jpg" that needs the API host prepended in dev.
// In prod the bundle rewrites this to https://storage.tficorg.org/.
const resolveAvatarUrl = (url?: string | null) =>
  !url ? undefined : url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL}${url}`;

interface OrgFleetOwner {
  id: number;
  username: string;
  avatarUrl?: string;
}

interface OrgFleetGroupOwner {
  fleetEntryId: number;
  owner: OrgFleetOwner;
  pledgedAt?: string;
  orgPledgeNote?: string;
  orgPledgeRole?: string;
}

interface OrgFleetGroupEntry {
  shipId?: number;
  shipName: string;
  manufacturer: string;
  role: string;
  imageUrl?: string;
  cargoCapacity?: number;
  crew?: number;
  count: number;
  owners: OrgFleetGroupOwner[];
}

interface NamedCount {
  name: string;
  count: number;
}

interface OrgFleetStats {
  totalShips: number;
  totalCargo: number;
  totalCrew: number;
  uniqueContributors: number;
  byManufacturer: NamedCount[];
  byRole: NamedCount[];
  byStatus: NamedCount[];
  topContributors: NamedCount[];
}

const OrgFleet: React.FC = () => {
  const [groups, setGroups] = useState<OrgFleetGroupEntry[]>([]);
  const [stats, setStats] = useState<OrgFleetStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterOwner, setFilterOwner] = useState('all');
  const [filterManufacturer, setFilterManufacturer] = useState('all');
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [fleetRes, statsRes] = await Promise.all([
        api.get('/org/fleet'),
        api.get('/org/fleet/stats'),
      ]);
      setGroups(Array.isArray(fleetRes.data) ? fleetRes.data : []);
      setStats(statsRes.data || null);
    } catch (err: any) {
      console.error('Error loading org fleet:', err);
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError("You don't have permission to view the org fleet. This page is restricted to Admin and Leader roles.");
      } else {
        setError(err?.response?.data?.message || err.message || 'Failed to load org fleet');
      }
    } finally {
      setLoading(false);
    }
  };

  const owners = useMemo(() => {
    const map = new Map<number, OrgFleetOwner>();
    groups.forEach(g => g.owners.forEach(o => {
      if (!map.has(o.owner.id)) map.set(o.owner.id, o.owner);
    }));
    return Array.from(map.values()).sort((a, b) => a.username.localeCompare(b.username));
  }, [groups]);

  const manufacturers = useMemo(
    () => Array.from(new Set(groups.map(g => g.manufacturer))).filter(Boolean).sort(),
    [groups]
  );

  const roles = useMemo(
    () => Array.from(new Set(groups.map(g => g.role))).filter(Boolean).sort(),
    [groups]
  );

  const filtered = useMemo(() => {
    let result = [...groups];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(g =>
        g.shipName.toLowerCase().includes(q) ||
        g.manufacturer.toLowerCase().includes(q) ||
        g.owners.some(o => o.owner.username.toLowerCase().includes(q)));
    }
    if (filterOwner !== 'all') {
      result = result.filter(g => g.owners.some(o => String(o.owner.id) === filterOwner));
    }
    if (filterManufacturer !== 'all') result = result.filter(g => g.manufacturer === filterManufacturer);
    if (filterRole !== 'all') result = result.filter(g => g.role === filterRole);
    return result;
  }, [groups, searchTerm, filterOwner, filterManufacturer, filterRole]);

  const formatPledgedAt = (iso?: string) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className={styles.fleetContainer}>
        <div className={styles.loading}><p>Loading org fleet...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.fleetContainer}>
        <div className={styles.emptyState}>
          <Flag size={64} />
          <h3>Cannot load org fleet</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.fleetContainer}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            <Flag className={styles.titleIcon} /> Org Fleet
          </h1>
          <p className={styles.subtitle}>
            Ships pledged by members for org-level use, grouped by hull. Owners control their own pledges from the <Link to="/my-fleet">My Fleet</Link> page.
          </p>
        </div>
        <div className={styles.headerButtons}>
          <Link to="/leader-dashboard/org-fleet/audit" className={styles.guideButton}>
            <FileText size={16} /> Audit Log
          </Link>
          <Link to="/leader-dashboard" className={styles.guideButton}>
            <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} /> Back to Dashboard
          </Link>
        </div>
      </div>

      {stats && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statContent}>
              <Rocket className={styles.statIcon} />
              <div>
                <div className={styles.statValue}>{stats.totalShips}</div>
                <div className={styles.statLabel}>Pledged Ships</div>
              </div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statContent}>
              <Users className={styles.statIcon} />
              <div>
                <div className={styles.statValue}>{stats.uniqueContributors}</div>
                <div className={styles.statLabel}>Contributors</div>
              </div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statContent}>
              <Package className={styles.statIcon} />
              <div>
                <div className={styles.statValue}>{stats.totalCargo.toLocaleString()} SCU</div>
                <div className={styles.statLabel}>Total Cargo</div>
              </div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statContent}>
              <Users className={styles.statIcon} />
              <div>
                <div className={styles.statValue}>{stats.totalCrew}</div>
                <div className={styles.statLabel}>Crew Slots</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.filterCard}>
        <div className={styles.filterRow}>
          <div className={styles.searchBox}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search ships, owners, manufacturers..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)}>
            <option value="all">All Contributors</option>
            {owners.map(o => (
              <option key={o.id} value={String(o.id)}>{o.username}</option>
            ))}
          </select>
          <select value={filterManufacturer} onChange={e => setFilterManufacturer(e.target.value)}>
            <option value="all">All Manufacturers</option>
            {manufacturers.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)}>
            <option value="all">All Roles</option>
            {roles.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <Flag size={64} />
          <h3>{groups.length === 0 ? 'No ships pledged yet' : 'No ships match these filters'}</h3>
          <p>
            {groups.length === 0
              ? 'Members can pledge ships from their My Fleet page. Pledged ships will appear here for fleet-composition planning.'
              : 'Try clearing one of the filters above.'}
          </p>
        </div>
      ) : (
        <div className={styles.shipGrid}>
          {filtered.map(g => {
            const key = `${g.shipId ?? 'noid'}-${g.shipName}-${g.manufacturer}`;
            return (
              <div key={key} className={`${styles.shipCard} ${styles.shipCardPledged}`}>
                <div className={styles.shipImageContainer}>
                  {g.imageUrl ? (
                    <img src={g.imageUrl} alt={g.shipName} className={styles.shipImage} />
                  ) : (
                    <div className={styles.shipImagePlaceholder} title="No image available for this ship">
                      <img src={tficLogo} alt="" className={styles.placeholderLogo} />
                      <span className={styles.placeholderLabel}>No image</span>
                    </div>
                  )}
                  <div className={styles.pledgeRibbon} title="Pledged to org fleet">
                    <Flag size={12} /> ORG
                  </div>
                  {g.count > 1 && (
                    <div className={styles.countBadge} title={`${g.count} members have pledged this ship`}>
                      ×{g.count}
                    </div>
                  )}
                </div>
                <div className={styles.shipBody}>
                  <h3 className={styles.shipName}>
                    {g.shipName}
                    {g.count > 1 && <span className={styles.shipNameCount}> ×{g.count}</span>}
                  </h3>
                  <p className={styles.shipManufacturer}>{g.manufacturer}</p>
                  <div className={styles.shipDetails}>
                    <div className={styles.shipMeta}>
                      {g.role && <span><strong>Role:</strong> {g.role}</span>}
                      {g.cargoCapacity != null && <span><strong>Cargo:</strong> {g.cargoCapacity} SCU</span>}
                      {g.crew != null && <span><strong>Crew:</strong> {g.crew}</span>}
                    </div>
                  </div>
                  <div className={styles.orgFleetOwnersList}>
                    <div className={styles.orgFleetOwnersHeader}>
                      Pledged by {g.count === 1 ? '1 member' : `${g.count} members`}
                    </div>
                    {g.owners.map(o => {
                      const resolvedAvatar = resolveAvatarUrl(o.owner.avatarUrl);
                      return (
                      <div key={o.fleetEntryId} className={styles.orgFleetOwnerRow}>
                        {resolvedAvatar ? (
                          <img
                            src={resolvedAvatar}
                            alt={o.owner.username}
                            className={styles.orgFleetGroupAvatar}
                            onError={(e) => {
                              const img = e.currentTarget;
                              img.style.display = 'none';
                              const sibling = img.nextElementSibling as HTMLElement | null;
                              if (sibling) sibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={styles.orgFleetGroupAvatarFallback}
                          style={{ display: resolvedAvatar ? 'none' : 'flex' }}
                        >
                          {o.owner.username.charAt(0).toUpperCase()}
                        </div>
                        <div className={styles.orgFleetOwnerInfo}>
                          <button
                            type="button"
                            className={styles.orgFleetOwnerName}
                            onClick={() => setFilterOwner(String(o.owner.id))}
                            title={`Filter to only ships from ${o.owner.username}`}
                          >
                            {o.owner.username}
                          </button>
                          {o.pledgedAt && (
                            <span className={styles.orgFleetOwnerMeta}>
                              <Clock size={11} /> {formatPledgedAt(o.pledgedAt)}
                            </span>
                          )}
                          {(o.orgPledgeRole || o.orgPledgeNote) && (
                            <div className={styles.orgFleetOwnerNote}>
                              {o.orgPledgeRole && <span><strong>For:</strong> {o.orgPledgeRole}</span>}
                              {o.orgPledgeNote && (
                                <span className={styles.orgFleetOwnerNoteText} title={o.orgPledgeNote}>
                                  "{o.orgPledgeNote}"
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrgFleet;
